"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface FillBlankCardProps {
  data: {
    template: string;
    blanks: Array<{
      options: string[];
      correctIndex: number;
    }>;
    explanation: string;
  };
  onAnswer: (correct: boolean) => void;
}

export function FillBlankCard({ data, onAnswer }: FillBlankCardProps) {
  const { template, blanks, explanation } = data;
  const [selections, setSelections] = useState<(number | null)[]>(
    () => new Array(blanks.length).fill(null)
  );
  const [submitted, setSubmitted] = useState(false);

  const segments = template.split("___");

  function handleSelect(blankIndex: number, optionIndex: number) {
    if (submitted) return;
    setSelections((prev) => {
      const next = [...prev];
      next[blankIndex] = optionIndex;
      return next;
    });
  }

  function handleCheck() {
    if (selections.some((s) => s === null)) return;
    setSubmitted(true);
    const allCorrect = blanks.every(
      (blank, i) => selections[i] === blank.correctIndex
    );
    onAnswer(allCorrect);
  }

  function getChipClasses(blankIndex: number, optionIndex: number) {
    const blank = blanks[blankIndex]!;
    const isSelected = selections[blankIndex] === optionIndex;
    const isCorrect = optionIndex === blank.correctIndex;

    if (submitted) {
      if (isSelected && isCorrect) {
        return "bg-green-100 text-green-800";
      }
      if (isSelected && !isCorrect) {
        return "bg-red-100 text-red-800";
      }
      if (!isSelected && isCorrect && selections[blankIndex] !== blank.correctIndex) {
        return "bg-muted text-muted-foreground ring-2 ring-green-500";
      }
      return "bg-muted text-muted-foreground opacity-50";
    }

    if (isSelected) {
      return "bg-primary text-primary-foreground";
    }

    return "bg-muted text-muted-foreground hover:bg-accent";
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg leading-relaxed">
        {segments.map((segment, i) => (
          <span key={i}>
            {segment}
            {i < blanks.length && (
              <span className="inline-flex flex-wrap items-center gap-2 mx-1 align-baseline">
                {blanks[i]!.options.map((option, optIdx) => (
                  <button
                    key={optIdx}
                    type="button"
                    disabled={submitted}
                    onClick={() => handleSelect(i, optIdx)}
                    className={cn(
                      "inline-flex px-3 py-1 rounded-full text-sm font-medium transition",
                      submitted ? "cursor-default" : "cursor-pointer",
                      getChipClasses(i, optIdx)
                    )}
                  >
                    {option}
                  </button>
                ))}
              </span>
            )}
          </span>
        ))}
      </p>

      {submitted && (
        <div className="bg-muted rounded-lg p-4 mt-4">
          <p className="font-medium mb-1">Explanation</p>
          <p className="text-sm leading-relaxed">{explanation}</p>
        </div>
      )}

      {!submitted && (
        <Button
          className="w-full"
          disabled={selections.some((s) => s === null)}
          onClick={handleCheck}
        >
          Check
        </Button>
      )}
    </div>
  );
}
